import * as React from "react"
import { X } from "lucide-react"

import { cn } from "@/lib/utils"
import {
  sourceLabel,
  sourceNetwork,
  type FeedSubtype,
  type SourceType,
} from "@/shadcn/components/settings/settings-vocab"
import {
  SettingsFormProvider,
  type SettingsSection,
} from "@/shadcn/components/settings/settings-form"
import {
  FormFooter,
  SettingsBody,
  SettingsAvatar,
  type SettingsPageProps,
} from "@/shadcn/components/settings/settings-page"
import { Button } from "@/shadcn/components/ui/button"
import {
  Dialog,
  DialogContent,
  DialogTitle,
} from "@/shadcn/components/ui/dialog"

/*
 * A SOURCE'S SETTINGS ON A DIALOG SURFACE — legacy's `InputModal`, which
 * complex shows while a route is being built so the whole source can be
 * configured before it exists.
 *
 * NOT IN THE REDESIGN. The kit covers editing and not creating: its five
 * screens are the two source pages, the destination page, the override dialog
 * and the route page. This is the sixth surface, and the copy and chrome below
 * are therefore mine rather than rulings — see the branch notes.
 *
 * Built by COMPOSITION rather than as a new screen, which is what the split
 * shell is for: `SettingsBody` is the part every settings surface shares — tab
 * strip, dirty dots, fields, skeletons — and each surface wraps its own chrome
 * around it. `destination-override.tsx` is the sibling this follows almost
 * line for line; the differences are that a source has no inherit row and no
 * remove action, and that its title names the source rather than an account.
 *
 * The fields come from the SAME `buildFeedSections` the two source pages use,
 * with `create: true`. Six of the seven tabs are identical to editing; only
 * the first differs, and it differs in the data rather than here.
 */

type SourceSettingsDialogProps = Omit<
  Partial<SettingsPageProps>,
  | "backLabel"
  | "onBack"
  | "note"
  | "onDismissNote"
  | "actions"
  | "showDelete"
  | "deleteTitle"
  | "deleteDescription"
  | "deleteLabel"
  | "onDelete"
> & {
  sections: SettingsSection[]
  /** Closes the dialog without keeping the edits. */
  onClose?: () => void
  source?: SourceType
  subtype?: FeedSubtype
  /** What the source is called so far. Blank until it is named. */
  sourceName?: string
  /**
   * The feed's icon, as legacy's avatar shows it: `input.icon`. Usually
   * absent while the source is being created; the avatar then shows
   * initials, or a blank disc until the source is named.
   */
  avatarUrl?: string
  contentClassName?: string
}

export default function SourceSettingsDialog({
  sections,
  source = "rss",
  subtype,
  sourceName,
  avatarUrl,
  title,
  meta,
  className,
  contentClassName,
  dirty,
  saving,
  loading,
  onChange,
  onSave,
  onCancel,
  onClose,
  ...props
}: SourceSettingsDialogProps) {
  const isDirty = dirty ?? Boolean(props.dirtySections?.length)
  const label = sourceLabel(source, subtype)
  /*
   * The name leads and the settings noun sits under it, as on the Source
   * settings page (Emily, 2026-10-01). A source being created has no name
   * yet, so until it does the title stays the settings noun and the line
   * under it says what to do - the header never shows an empty title.
   */
  const heading = title ?? (sourceName ? sourceName : `${label} settings`)
  const subheading =
    meta ??
    (sourceName
      ? `Source settings · ${label}`
      : "Set this source up before it starts posting.")

  return (
    <Dialog
      open
      onOpenChange={(open) => {
        if (!open) onClose?.()
      }}
    >
      <DialogContent
        showCloseButton={false}
        aria-describedby={undefined}
        className={cn(
          "tw:gap-0 tw:rounded-none tw:bg-transparent tw:p-0 tw:ring-0 tw:shadow-none tw:sm:max-w-none",
          contentClassName
        )}
      >
        <DialogTitle className="tw:sr-only">{heading}</DialogTitle>
        <SettingsFormProvider onChange={onChange}>
          <div
            className={cn(
              /* 640px, matching the override dialog, and for its reason: the
                 rows are label-left / control-right, so a wider box only
                 pushes each control further from what labels it. */
              "tw:relative tw:mx-auto tw:flex tw:max-h-[calc(100dvh-2rem)] tw:w-full tw:max-w-[640px] tw:flex-col tw:overflow-hidden tw:rounded-xl tw:border tw:border-border tw:bg-card tw:font-sans tw:text-card-foreground tw:shadow-lg",
              className
            )}
          >
            <div className="tw:flex tw:shrink-0 tw:items-start tw:gap-4 tw:border-b tw:border-border tw:p-4 tw:md:p-6">
              {/* The source's avatar, as the source pages and the override
                  dialog draw it, beside both header lines. */}
              <div className="tw:flex tw:min-w-0 tw:flex-1 tw:items-center tw:gap-3">
                <SettingsAvatar
                  name={sourceName ?? ""}
                  avatarUrl={avatarUrl}
                  network={sourceNetwork(source, subtype)}
                />
                <div className="tw:flex tw:min-w-0 tw:flex-1 tw:flex-col tw:gap-1">
                  <h2 className="tw:text-lg tw:leading-tight tw:font-semibold tw:text-foreground">
                    {heading}
                  </h2>
                  <p className="tw:text-sm tw:text-muted-foreground">
                    {subheading}
                  </p>
                </div>
              </div>
              <Button
                type="button"
                variant="ghost"
                size="icon-sm"
                aria-label="Close"
                className="tw:-mt-1 tw:-mr-1 tw:shrink-0 tw:text-muted-foreground tw:hover:text-foreground"
                onClick={onClose}
              >
                <X />
              </Button>
            </div>

    {/*
    * BODY SCROLLS, HEADER AND FOOTER STAY.
    *
    * THE CARD IS CAPPED, NOT THE BODY. A fixed `max-h` on the scroll box
    * cannot know what the header and footer above and below it cost, so
    * the card grew past the viewport and `DialogContent`, which centres
    * with `top-1/2 -translate-y-1/2`, clipped it at both ends — and
    * nothing scrolled, because the overflow sat on a box that never
    * exceeded its own cap. At 550px tall the whole footer was gone.
    *
    * So the CARD takes the viewport cap and the body is the shrinkable
    * part: `flex-1` to take what is left, and `min-h-0` because a flex
    * item will not shrink below its content without it — the same trap
    * that squashed the inherit card, one level up.
    *
    * `dvh` rather than `vh` so a mobile browser's collapsing toolbar is
    * counted; the 2rem matches the horizontal inset `DialogContent`
    * already keeps.
    *
    * THE KIT COPY DIFFERS HERE, and is meant to: its 520px exists
    * because the preview harness sizes its iframe FROM the measured
    * content, so a viewport unit there feeds the iframe's own height
    * back into the content and never settles. Its own note says prod
    * should use its own height handling instead of that number. This is
    * that.
    */}
            <div className="tw:min-h-0 tw:flex-1 tw:overflow-y-auto">
              <div className="tw:flex tw:flex-col tw:gap-6 tw:p-4 tw:md:p-6">
                <SettingsBody sections={sections} loading={loading} {...props} />
              </div>
            </div>

            <div className="tw:shrink-0 tw:border-t tw:border-border tw:p-4 tw:md:p-6">
              <FormFooter
                dirty={isDirty}
                saving={saving}
                onSave={onSave}
                onCancel={onCancel ?? onClose}
              />
            </div>
          </div>
        </SettingsFormProvider>
      </DialogContent>
    </Dialog>
  )
}
